import { useId } from 'react';
import { Link } from 'react-router';
import { FormattedMessage, useIntl } from 'react-intl';
import { useFormat } from '@/i18n/useFormat';
import { useLocale } from '@/i18n/useLocale';
import { useNickname } from '@/i18n/useNickname';
import { paths } from '@/app/paths';
import { Coin, Odometer } from '@/ui';
import { Emblem } from '@/art/Emblem';
import { rasterArt } from '@/art/raster';
import { gameName } from '@/features/achievements/text';
import { useJackpot } from './store';
import s from './jackpot.module.css';

const SHOWN_WINNERS = 3;

/**
 * Lobby block of «乐游 Jackpot» (top of the lobby): the live pool, the must-drop ceiling, the last winners and the way
 * to the check on /fair. Only the pool and the ceiling — no "hot / close to dropping" hints (honest play).
 */
export function JackpotBanner({ compact }: { compact?: boolean }) {
  const jp = useJackpot();
  const f = useFormat();
  const intl = useIntl();
  const l = useLocale();
  const nick = useNickname();
  const titleId = useId();
  if (!jp) return null;
  const how = `${paths.fair(l)}#jackpot`;
  // the medallion (Codex emblem) leads the block; without the raster the block keeps its two columns
  const emblem = rasterArt('emblems', 'jackpot') ? '' : undefined;
  if (compact) {
    // phone: a slim block (≈ 110 px) so Liftoff stays above the fold — the title and the way to the check, the pool
    // with its ceiling, one last winner in one line; the long description lives on /fair#jackpot
    const last = jp.winners[0];
    return (
      <section className={s.mini} aria-labelledby={titleId} data-testid="jackpot-banner" data-emblem={emblem}>
        <Emblem name="jackpot" size={36} className={s.miniEmblem} />
        <div className={s.miniHead}>
          <h2 id={titleId} className={s.bannerTitle}>
            <FormattedMessage id="jackpot.title" />
          </h2>
          <Link to={how} className={s.miniLink}>
            <FormattedMessage id="jackpot.howLink" />
          </Link>
        </div>
        <div className={s.miniPool}>
          <span className={s.bannerPool}>
            <Coin size={20} />
            <Odometer value={jp.pool} format={(n) => f.tokens(n, { decimals: 0 })} flash={false} className={s.miniAmount} />
          </span>
          <span className={s.miniCap}>
            <FormattedMessage id="jackpot.mustDrop" values={{ cap: f.tokens(jp.cap, { decimals: 0 }) }} />
          </span>
        </div>
        <p className={s.miniWinner}>
          {last ? (
            <>
              <span className={s.miniLabel}>
                <FormattedMessage id="jackpot.lastWin" />
              </span>
              <span className={s.miniName}>{nick(last.nickname)}</span>
              <span className={s.miniGame} aria-hidden="true">
                ·
              </span>
              <span className={s.miniGame}>{gameName(intl, last.game)}</span>
              <span className={s.miniGame} aria-hidden="true">
                ·
              </span>
              <span className={s.winnerAmount}>{f.tokens(last.amount, { decimals: 0 })}</span>
            </>
          ) : (
            <span className={s.sideEmpty}>
              <FormattedMessage id="jackpot.noWinners" />
            </span>
          )}
        </p>
      </section>
    );
  }
  const winners = jp.winners.slice(0, SHOWN_WINNERS);
  return (
    <section className={s.banner} aria-labelledby={titleId} data-testid="jackpot-banner" data-emblem={emblem}>
      <Emblem name="jackpot" size={88} className={s.bannerEmblem} />
      <div className={s.bannerMain}>
        <h2 id={titleId} className={s.bannerTitle}>
          <FormattedMessage id="jackpot.title" />
        </h2>
        <div className={s.bannerPool}>
          <Coin size={26} />
          <Odometer value={jp.pool} format={(n) => f.tokens(n, { decimals: 0 })} flash={false} className={s.bannerAmount} />
        </div>
        <p className={s.bannerCap}>
          <FormattedMessage id="jackpot.mustDrop" values={{ cap: f.tokens(jp.cap, { decimals: 0 }) }} />
        </p>
        <p className={s.bannerNote}>
          <FormattedMessage id="jackpot.note" />
        </p>
      </div>
      <div className={s.bannerSide}>
        <span className={s.sideCap}>
          <FormattedMessage id="jackpot.winners" />
        </span>
        {winners.length === 0 ? (
          <span className={s.sideEmpty}>
            <FormattedMessage id="jackpot.noWinners" />
          </span>
        ) : (
          <ul className={s.winners}>
            {winners.map((w) => (
              <li key={w.proof.betId} className={s.winner}>
                <span className={s.winnerName}>{nick(w.nickname)}</span>
                <span className={s.winnerAmount}>{f.tokens(w.amount, { decimals: 0 })}</span>
                <span className={s.winnerMeta}>
                  {gameName(intl, w.game)} · {f.date(w.at, { day: 'numeric', month: 'short' })}
                </span>
              </li>
            ))}
          </ul>
        )}
        <Link to={how} className={s.sideLink}>
          <FormattedMessage id="jackpot.howLink" />
        </Link>
      </div>
    </section>
  );
}
